import { Component } from 'react';
import { Empty } from '../ui';

/** A lazy page whose code could not be fetched — usually a deploy since this tab loaded. */
const isChunkLoadError = (error) => error?.name === 'ChunkLoadError'
  || /Loading (CSS )?chunk [\w-]+ failed/i.test(String(error?.message || ''));

/**
 * The page area's own error boundary, inside the shell.
 *
 * The app's only other boundary wraps the whole router, above the shell. A page
 * that threw while rendering — or a lazy page whose chunk is gone after a
 * deploy — unwound to it and took the shell down too: the copilot provider
 * unmounted, the room closed, the conversation and any open Run / Cancel card
 * were lost, and pending agent requests were dropped with no report. Caught
 * here, only the page area is replaced; the sidebar, the top bar and the
 * copilot stay as they were.
 *
 * `resetKey` is the pathname, so opening another screen clears the error. A
 * chunk that failed to load is different: React caches the rejected import, so
 * trying again renders the same error, and only a reload fetches the new build.
 */
export default class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, key: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    return { error: error || new Error('The page failed to render') };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.key) return { error: null, key: props.resetKey };
    return null;
  }

  componentDidCatch(error, info) {
    console.error('[page] render failed', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const stale = isChunkLoadError(error);
    return (
      <div className="card" role="alert">
        <Empty
          title={stale ? 'This screen has been updated' : 'This screen could not be shown'}
          hint={stale
            ? 'A newer version of the app is available. Reload to open this screen; the assistant reconnects on its own.'
            : 'Something went wrong while drawing it. The rest of the app, and the assistant, still work — open another screen or try again.'}
          action={stale ? (
            <button type="button" className="btn pri" onClick={() => window.location.reload()}>Reload</button>
          ) : (
            <button type="button" className="btn" onClick={() => this.setState({ error: null })}>Try again</button>
          )}
        />
      </div>
    );
  }
}
